import React, { useMemo, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import type { VisualizationProps, ChartData, TableRow } from './types';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import './Visualization.css';

/**
 * Visualization Component
 * Renders charts based on visualization data from Cortex
 *
 * Optimizations:
 * - Wrapped with React.memo to prevent unnecessary re-renders
 * - useMemo for expensive chart configuration computations
 * - Memoized helper functions
 */
const VisualizationComponent = ({ data }: VisualizationProps) => {
  if (!data) return null;

  const { type, data: chartData, table_data } = data;

  // Chart color palettes - defined outside useMemo since they're static
  const borderColorPalette: string[] = useMemo(() => [
    'rgba(102, 126, 234, 1)',
    'rgba(237, 100, 166, 1)',
    'rgba(255, 159, 64, 1)',
    'rgba(75, 192, 192, 1)',
    'rgba(153, 102, 255, 1)',
    'rgba(255, 99, 132, 1)',
  ], []);

  // Memoize ApexCharts series data preparation
  const apexSeries = useMemo((): any[] => {
    if (!chartData || !chartData.datasets) {
      return [];
    }

    if (type === 'doughnut') {
      return chartData.datasets[0]?.data || [];
    }

    if (type === 'scatter') {
      return chartData.datasets.map((dataset, idx) => ({
        name: dataset.label,
        data: dataset.data.map((value, index) => ({
          x: chartData.labels[index],
          y: value,
        })),
      }));
    }

    return chartData.datasets.map((dataset, idx) => ({
      name: dataset.label,
      data: dataset.data,
    }));
  }, [chartData, type]);

  // Memoize ApexCharts options
  const apexOptions = useMemo((): ApexOptions => {
    const baseOptions: ApexOptions = {
      chart: {
        toolbar: {
          show: true,
        },
        zoom: {
          enabled: true,
        },
      },
      colors: borderColorPalette,
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: chartData?.datasets ? chartData.datasets.length > 1 : false,
        position: 'top',
      },
      tooltip: {
        shared: true,
        intersect: false,
      },
    };

    if (type === 'line' || type === 'area') {
      return {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: 'line',
        },
        stroke: {
          curve: 'smooth',
          width: 2,
        },
        fill: {
          type: type === 'area' ? 'gradient' : 'solid',
          opacity: type === 'area' ? 0.4 : 1,
          gradient: type === 'area' ? {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.3,
          } : undefined,
        },
        xaxis: {
          categories: chartData?.labels || [],
        },
        yaxis: {
          title: {
            text: undefined,
          },
        },
      };
    }

    if (type === 'bar' || type === 'stackedBar') {
      return {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: 'bar',
          stacked: type === 'stackedBar',
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
          },
        },
        xaxis: {
          categories: chartData?.labels || [],
        },
        yaxis: {
          title: {
            text: undefined,
          },
        },
      };
    }

    if (type === 'horizontalBar') {
      return {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: 'bar',
        },
        plotOptions: {
          bar: {
            horizontal: true,
          },
        },
        xaxis: {
          categories: chartData?.labels || [],
        },
        yaxis: {
          title: {
            text: undefined,
          },
        },
      };
    }

    if (type === 'doughnut') {
      return {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: 'donut',
        },
        labels: chartData?.labels || [],
        legend: {
          show: true,
          position: 'right',
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
            },
          },
        },
      };
    }

    if (type === 'scatter') {
      return {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: 'scatter',
        },
        xaxis: {
          type: 'category',
          categories: chartData?.labels || [],
        },
        yaxis: {
          title: {
            text: undefined,
          },
        },
      };
    }

    return baseOptions;
  }, [chartData, type, borderColorPalette]);

  // Memoize chart type for ApexCharts
  const chartType = useMemo(() => {
    return type === 'doughnut' ? 'donut' :
      type === 'bar' || type === 'stackedBar' || type === 'horizontalBar' ? 'bar' :
      type === 'scatter' ? 'scatter' :
      'line';
  }, [type]);

  // Format cell value (handle dates) - memoized
  const formatCellValue = useCallback((value: string | number): string | number => {
    if (typeof value !== 'string') return value;

    // Try to parse as date
    const dateValue = new Date(value);
    if (!isNaN(dateValue.getTime()) && value.match(/\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4}|T\d{2}:\d{2}/)) {
      // Format as Y-m-d (YYYY-MM-DD)
      const year = dateValue.getFullYear();
      const month = String(dateValue.getMonth() + 1).padStart(2, '0');
      const day = String(dateValue.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return value;
  }, []);

  // Memoize table columns
  const tableColumns = useMemo(() => {
    if (!table_data || table_data.length === 0) return [];
    return Object.keys(table_data[0]);
  }, [table_data]);

  // Check if chart data is valid
  const hasValidChartData = chartData && chartData.labels && chartData.datasets && apexSeries.length > 0;

  return (
    <div className="visualization-container">
      {type !== 'table' && chartData && (
        <div className="chart-container">
          {hasValidChartData ? (
            <ReactApexChart
              options={apexOptions}
              series={apexSeries}
              type={chartType}
              height={350}
            />
          ) : (
            <div className="chart-error">No chart data available</div>
          )}
        </div>
      )}
      {table_data && table_data.length > 0 && (
        <details className="table-details" open={type === 'table'}>
          <summary className="table-summary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
              <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2"/>
              <line x1="3" y1="9" x2="21" y2="9" strokeWidth="2"/>
              <line x1="3" y1="15" x2="21" y2="15" strokeWidth="2"/>
              <line x1="9" y1="3" x2="9" y2="21" strokeWidth="2"/>
            </svg>
            View Data Table ({table_data.length} rows)
          </summary>
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  {tableColumns.map((col, idx) => (
                    <th key={idx}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table_data.map((row: TableRow, rowIdx: number) => (
                  <tr key={rowIdx}>
                    {tableColumns.map((col, colIdx) => (
                      <td key={colIdx}>{formatCellValue(row[col])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
};

// PropTypes validation
VisualizationComponent.propTypes = {
  data: PropTypes.shape({
    type: PropTypes.oneOf(['line', 'bar', 'horizontalBar', 'stackedBar', 'area', 'doughnut', 'scatter', 'table']).isRequired,
    data: PropTypes.shape({
      labels: PropTypes.arrayOf(PropTypes.string),
      datasets: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          data: PropTypes.arrayOf(PropTypes.number),
          backgroundColor: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
          borderColor: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
          borderWidth: PropTypes.number,
          fill: PropTypes.bool,
        })
      ),
    }),
    table_data: PropTypes.arrayOf(PropTypes.object),
  }),
};

// Wrap with memo for performance optimization
const Visualization = memo(VisualizationComponent);

// Display name for debugging
Visualization.displayName = 'Visualization';

export default Visualization;
